import type { QueryClient } from "@tanstack/react-query";
import { createSessionChannel } from "@a2z/browser-session";
import { fetchSessionAfterBindingFailure } from "@/lib/api/client";
import { finishPrivateSessionChange, getPrivateSessionState, invalidatePrivateSession } from "@/lib/api/private-session";
import { signOutAdminFirebaseAuth } from "@/lib/firebase/auth-session";
import { queryKeys } from "@/lib/query/core/query-keys";
import type { AdminView } from "@/lib/api/types";
export const sessionChannel = createSessionChannel("admin");
export async function resetPrivateState(client: QueryClient, generation = getPrivateSessionState().generation) {
  if (generation !== getPrivateSessionState().generation) return;
  await client.cancelQueries({ queryKey: ["admin"] });
  if (generation !== getPrivateSessionState().generation) return;
  client.getMutationCache().clear();
  client.removeQueries({ queryKey: ["admin"] });
}
export async function inspectPrivateBinding() {
  const response = await fetchSessionAfterBindingFailure();
  if (response.status === 401 || response.status === 403) return undefined;
  if (!response.ok) throw new Error("Session inspection failed");
    const payload = await response.json() as { admin?: AdminView; firebaseToken?: string | null };
    if (!payload.admin?.id) throw new Error("Invalid session inspection");
    return payload;
}
export async function recoverPrivateBinding(client: QueryClient, generation: number) {
  const payload = await inspectPrivateBinding();
  if (generation !== getPrivateSessionState().generation) throw new DOMException("Session changed", "AbortError");
  await resetPrivateState(client, generation);
  if (generation !== getPrivateSessionState().generation) throw new DOMException("Session changed", "AbortError");
  const storeSlug = payload ? undefined : undefined;
  finishPrivateSessionChange(storeSlug, payload?.admin?.id);
    if (payload) client.setQueryData(queryKeys.admin.session(), payload);
}
export function installSessionChannel(client: QueryClient) {
  sessionChannel.install(() => {
    invalidatePrivateSession();
    void resetPrivateState(client);
    void signOutAdminFirebaseAuth().catch(() => undefined);
  }, () => recoverPrivateBinding(client, getPrivateSessionState().generation));
}
